import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { withStyles } from "@mui/styles";
import accountIcon from "@iconify/icons-mdi/account";
import calendarIcon from "@iconify/icons-mdi/calendar-today";
import closeIcon from "@iconify/icons-mdi/close";
import downIcon from "@iconify/icons-mdi/menu-down";
import { InlineIcon } from "@iconify/react";
import { FormikProps } from "formik";
import {
  MyNewsItemFormFilterEmployees,
  MyNewsItemFormFilterValidity,
} from "my-news/components";
import { OcStyledBadge } from "oc/components";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

interface MyNewsItemFormFilterProps {
  formik: FormikProps<any>;
  roleFilters: any;
  handleAddFilter: (...args: any[]) => void;
  handleRemoveFilter: (...args: any[]) => void;
  resetRoleFilters: () => void;
  contract: any;
  initialValues: any;
  initialRoleFilter: any;
  published: boolean;
}

export const MyNewsItemFormFilter = ({
  formik,
  roleFilters,
  handleAddFilter,
  handleRemoveFilter,
  resetRoleFilters,
  contract,
  initialValues,
  initialRoleFilter,
  published,
}: MyNewsItemFormFilterProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [validityOpen, setValidityOpen] = useState(false);
  const [employeesOpen, setEmployeesOpen] = useState(false);
  const isMobile = useOcIsMobile();

  const StyledButton = withStyles({
    root: {
      border: "2px solid #212121 !important",
      color: "#212121 !important",
      "&:hover": {
        border: "2px solid #212121 !important",
        backgroundColor: "#C6CACC",
      },
      "&:active": {
        border: "2px solid #212121 !important",
      },
      minWidth: "20px !important",
      paddingLeft: isMobile ? "17px !important" : "8px",
    },
  })(Button);

  const handlePriorityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("priority", event.target.checked ? "high" : "normal");
  };

  const handleValidityOpen = () => {
    if (employeesOpen) {
      setEmployeesOpen(false);
    }
    setValidityOpen(!validityOpen);
  };

  const handleEmployeesOpen = () => {
    if (validityOpen) {
      setValidityOpen(false);
    }
    setEmployeesOpen(!employeesOpen);
  };

  const handleResetFilters = () => {
    resetRoleFilters();
    formik.setFieldValue("priority", initialValues.priority);
    formik.setFieldValue("validFrom", initialValues.validFrom);
    formik.setFieldValue("validTo", initialValues.validTo);
    formik.setTouched(
      Object.assign(formik.touched, {
        validTo: false,
      }),
    );
  };

  const buttonDisabled =
    formik.values.priority === initialValues.priority &&
    formik.values.validTo === initialValues.validTo &&
    _.isEqual(formik.values.validFrom, initialValues.validFrom) &&
    _.isEqual(initialRoleFilter, roleFilters);

  const hasValidFromError =
    formik.touched && formik.touched.validFrom && formik.errors.validFrom;
  const isEqualRoleFilters = _.isEqual(initialRoleFilter, roleFilters);

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        alignItems={isMobile ? "flex-end" : "center"}
        justifyContent="space-between"
        style={{
          marginBottom: "16px",
          borderRadius: "4px",
          border: `1px solid ${theme.palette.gray60.main}`,
          backgroundColor: theme.palette.gray30.main,
          height: isMobile ? "auto" : "58px",
          padding: "0 16px",
        }}
      >
        <Box
          style={{
            display: "flex",
            alignItems: isMobile ? "flex-start" : "center",
            flexDirection: isMobile ? "column" : "row",
            width: isMobile ? "100%" : "auto",
            backgroundColor: theme.palette.gray30.main,
            height: isMobile ? "auto" : "58px",
          }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Box marginRight="16px">
              <Typography
                variant="button"
                style={{
                  color: theme.palette.gray60.main,
                }}
              >
                {t("OC_SETTINGS")}
              </Typography>
            </Box>
            <Box
              width="1px"
              height="36px"
              style={{
                backgroundColor: theme.palette.gray40.main,
              }}
            />
            <Box
              id="my-news-filter-item-validity"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: "0px 16px",
                height: "56px",
                backgroundColor: validityOpen
                  ? theme.palette.white.main
                  : theme.palette.gray30.main,
              }}
            >
              <OcStyledBadge
                id="styled-badge"
                showBadge={!!hasValidFromError}
                module="MyNews"
                badgeContent="!"
                color="primary"
              >
                <InlineIcon
                  width="24px"
                  icon={calendarIcon}
                  color={theme.palette.darkBase.main}
                />
              </OcStyledBadge>
              <Box width="10px" />
              <Typography
                style={{
                  lineHeight: "normal",
                }}
              >
                {t("OC_VALIDITY")}
              </Typography>
              <Box width="10px" />
              <InlineIcon
                id="my-news-filter-item-validity-icon"
                width="24px"
                icon={downIcon}
                color={theme.palette.darkBase.main}
                onClick={handleValidityOpen}
                style={{
                  transform: validityOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                  cursor: "pointer",
                }}
              />
            </Box>
            <Box
              width="1px"
              height="36px"
              style={{
                backgroundColor: theme.palette.gray40.main,
              }}
            />
          </Box>
          {isMobile && <Box height="10px" />}
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Box
              id="my-news-filter-item-employees"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: "0px 16px",
                height: "56px",
                backgroundColor: employeesOpen
                  ? theme.palette.white.main
                  : theme.palette.gray30.main,
                marginLeft: isMobile ? "-16px" : "0px",
                borderBottomLeftRadius: isMobile ? "4px" : "0px",
              }}
            >
              <OcStyledBadge
                id="styled-badge"
                showBadge={!isEqualRoleFilters}
                module="MyNews"
                badgeContent="✓"
                color="primary"
              >
                <InlineIcon
                  width="24px"
                  icon={accountIcon}
                  color={theme.palette.darkBase.main}
                />
              </OcStyledBadge>
              <Box width="10px" />
              <Typography
                style={{
                  lineHeight: "normal",
                }}
              >
                {t("OC_EMPLOYEES")}
              </Typography>
              <Box width="10px" />
              <InlineIcon
                id="my-news-filter-item-employees-icon"
                width="24px"
                icon={downIcon}
                color={theme.palette.darkBase.main}
                onClick={handleEmployeesOpen}
                style={{
                  transform: employeesOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                  cursor: "pointer",
                }}
              />
            </Box>
            <Box
              width="1px"
              height="36px"
              style={{
                backgroundColor: theme.palette.gray40.main,
              }}
            />
            <Box marginLeft="16px">
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="checked-important"
                      checked={formik.values.priority === "high" ? true : false}
                      onChange={handlePriorityChange}
                      name="checkedB"
                      color="primary"
                      style={{
                        color: "#212121",
                      }}
                    />
                  }
                  label={t("MY_NEWS_IMPORTANT")}
                />
              </FormGroup>
            </Box>
            <Box
              width="1px"
              height="36px"
              style={{
                backgroundColor: theme.palette.gray40.main,
              }}
            />
          </Box>
        </Box>
        {!buttonDisabled && (
          <Box>
            <StyledButton
              id="reset-button"
              variant="outlined"
              disableElevation
              onClick={handleResetFilters}
              startIcon={<InlineIcon icon={closeIcon} />}
            >
              {!isMobile && t("OC_RESET")}
            </StyledButton>
            {isMobile && <Box height="10px" />}
          </Box>
        )}
      </Box>
      <MyNewsItemFormFilterValidity
        formik={formik}
        open={validityOpen}
        isMobile={isMobile}
        published={published}
      />
      <MyNewsItemFormFilterEmployees
        open={employeesOpen}
        handleAddFilter={handleAddFilter}
        handleRemoveFilter={handleRemoveFilter}
        roleFilters={roleFilters}
        product="MyNews"
        role="editor"
        contract={contract}
        isMobile={isMobile}
      />
    </>
  );
};

export default MyNewsItemFormFilter;
